import { join, partial } from '@quromedical/utils';
import type {
  BundleEntryRequest,
  BundleEntryResponse,
  FhirResource,
} from 'fhir/r4';

import type { Bundle, BundleEntry, MixedBundle, ResourceType } from '../types';
import { getResourceId } from './id';

const isSuccessStatusCode = (status: string | undefined) => {
  if (!status) {
    return false;
  }

  // using this since the Google API responds with `201 Created` instead of just `201` or `200`
  const is200 = status.startsWith('200');
  const is201 = status.startsWith('201');

  return is200 || is201;
};

/**
 * Checks if an operation is successful by checking if the `response` property is a `200` or `201`
 * @param bundle
 * @returns
 */
export const isOperationSuccessful = <T extends FhirResource>(
  bundle: Bundle<T> | MixedBundle<T>,
): boolean => {
  if (!bundle.entry) {
    return false;
  }

  const operationResults = bundle.entry.map(
    (e?: BundleEntry<T>) => e?.response,
  );

  const unsuccessfulResults = operationResults.filter(
    (r?: BundleEntryResponse) => !isSuccessStatusCode(r?.status),
  );

  return !unsuccessfulResults.length;
};

const createBundleMethodRequest = (
  method: BundleEntryRequest['method'],
  resourceType: ResourceType,
  resourceId?: string,
): BundleEntryRequest => {
  const url = resourceId ? join([resourceType, resourceId], '/') : resourceType;

  return {
    url,
    method,
  };
};

export const appendRequestToEntry = <T extends FhirResource>(
  method: BundleEntryRequest['method'],
  bundleEntry: BundleEntry<T>,
  fullUrl?: string,
): BundleEntry<T> => {
  const resourceId = getResourceId(bundleEntry.resource);

  const request = createBundleMethodRequest(
    method,
    bundleEntry.resource.resourceType,
    resourceId,
  );

  return {
    ...bundleEntry,
    request,
    fullUrl,
  };
};

export const mapResourceToEntry = <T extends FhirResource>(
  resource: T,
): BundleEntry<T> => ({
  resource,
});

export const appendPostRequestToEntry = partial(appendRequestToEntry, 'POST');

export const appendPutRequestToEntry = partial(appendRequestToEntry, 'PUT');

/**
 * Get the resource entries from a bundle if they exist, otherwise return an empty array
 */
export const getResourcesFromBundle = <T extends FhirResource>(
  bundle?: Bundle<T>,
): T[] => {
  if (!bundle?.entry) {
    return [];
  }

  const resources = bundle.entry.map((e) => e.resource);

  return resources;
};

/**
 * Create a `transaction` bundle with the given entries
 * @param entry
 * @returns
 */
export const createBundle = <T extends FhirResource>(
  entry: Array<BundleEntry<T>>,
): MixedBundle<T> => ({
  resourceType: 'Bundle',
  type: 'transaction',
  entry,
});

export const createBundleEntry = <T extends FhirResource>(
  entryMethod: BundleEntryRequest['method'],
  resource: T,
  fullUrl?: string,
): BundleEntry<T> =>
  appendRequestToEntry(entryMethod, mapResourceToEntry(resource), fullUrl);

export const mapResourceToBundleEntry =
  <T extends FhirResource>(
    entryMethod: BundleEntryRequest['method'],
    fullUrl?: string,
  ) =>
  (resource: T) =>
    createBundleEntry<T>(entryMethod, resource, fullUrl);
